import { PaginationPlusOne } from '@/components/DataTable/PaginationPlusOne';
import { useGet } from '@/query/fetchHooks';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import {
  InputText,
  IconButton,
  Button,
  FilterList,
  TEOLoader,
} from '@teo/components';
import { Search, Tag, ChevronUp, ChevronDown } from '@teo/components/icons';
import { AddImageToLibrary } from '../AddImageToLibrary';
import { newUid, parseFilter } from '@/util';
import { NewImageToLibrary } from '../NewImageToLibrary';
import i18n from 'i18next';

const ImageLibrary = ({ authUser, useTeoLib }) => {
  const { t } = useTranslation();
  const [loadImages, setLoadImages] = useState(true);
  const [isOpenAddLibraryModal, setIsOpenAddLibraryModal] = useState(false);
  const [isOpenNewLibraryModal, setIsOpenNewLibraryModal] = useState(false);
  const [activeImage, setActiveImage] = useState(null);
  const [filterInput, setFilterInput] = useState('');
  const [debouncedFilter, setDebouncedFilter] = useState('');
  const [primaryTags, setPrimaryTags] = useState(null);
  const [refresh, setRefresh] = useState(newUid);
  useEffect(() => {
    const t = setTimeout(
      () => setDebouncedFilter(parseFilter(filterInput)),
      250
    );
    return () => clearTimeout(t);
  }, [filterInput]);
  const [filterJoined, setFilterJoined] = useState('');
  const [selected, setSelected] = useState([]);
  const [sort, setSort] = useState([{ id: 'createdAt', desc: true }]);
  const [nmOfImage, setNmOfImage] = useState(0);

  const selectedTags = selected?.map((el) => el.value + ' ');

  useEffect(() => {
    const newFilterJoined =
      selectedTags.join(' ') + (debouncedFilter ? ` ${debouncedFilter}` : '');
    setFilterJoined(newFilterJoined);
  }, [debouncedFilter, selectedTags]);

  const FILTERS = [
    {
      label: t('pages.create_courses.Electricity'),
      value: t('pages.create_courses.Electricity'),
    },
    {
      label: t('pages.create_courses.Base'),
      value: t('pages.create_courses.Base'),
    },
    {
      label: t('pages.create_courses.Technology'),
      value: t('pages.create_courses.Technology'),
    },
    {
      label: t('pages.create_courses.Safety'),
      value: t('pages.create_courses.Safety'),
    },
    {
      label: t('pages.create_courses.Mechanics'),
      value: t('pages.create_courses.Mechanics'),
    },
  ];

  useGet('/tags', {
    primary: true,
    lang: i18n.language,
    onSuccess: (result) => {
      const tags = result?.data ?? [];
      const pTags = tags?.map((tag) => ({
        label: tag?.tag,
        value: tag?.tag,
      }));
      const missingCount = Math.max(0, 5 - pTags?.length);
      setPrimaryTags([...pTags.slice(0, 5), ...FILTERS.slice(0, missingCount)]);
    },
  });

  return (
    <div>
      <div className={`relative mb-6 sm:w-2/3 md:w-1/2`}>
        <InputText
          value={filterInput}
          onChange={(ev) => {
            setFilterInput(ev.target.value);
            setLoadImages(true);
          }}
          Icon={Search}
          placeholder={t('pages.create_edit_course.library.placeholder_search')}
        />
      </div>
      <div className="mb-6 flex flex-col justify-between gap-4 lg:flex-row lg:gap-0">
        <div className={`relative flex items-center `}>
          <IconButton Icon={Tag} variant="ghost" size="sm" className="mr-2" />
          {primaryTags && (
            <FilterList
              filters={primaryTags}
              onSelect={setSelected}
              selected={selected}
            />
          )}
        </div>
      </div>
      <div className="mb-4 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:gap-4 lg:gap-0">
        <div className="smorder-2 order-2 flex w-full items-center">
          <p className="flex items-center gap-1 text-base font-semibold text-black">
            {t('pages.create_library.images')}
            <span className="text-sm font-normal text-grey-06">
              {nmOfImage}
            </span>
          </p>
          <div className="ml-auto flex items-center sm:order-2">
            <Button
              className="flex items-center"
              variant="ghost"
              size="sm"
              onClick={() =>
                sort[0].desc
                  ? setSort([{ id: 'createdAt', desc: false }])
                  : setSort([{ id: 'createdAt', desc: true }])
              }
            >
              <div className="pr-2">{t('pages.create_courses.latest')}</div>
              {sort[0].desc ? (
                <ChevronUp className="w-5" />
              ) : (
                <ChevronDown className="w-5" />
              )}
            </Button>
          </div>
        </div>
        <Button
          className="ml-auto flex items-center gap-2 sm:order-3"
          onClick={() => setIsOpenNewLibraryModal(true)}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            data-lucide="image-plus"
            aria-hidden="true"
          >
            <path d="M16 5h6"></path>
            <path d="M19 2v6"></path>
            <path d="M21 11.5V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7.5"></path>
            <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"></path>
            <circle cx="9" cy="9" r="2"></circle>
          </svg>
          {t('pages.create_library.add_image')}
        </Button>
      </div>

      <div className=""></div>
      <div>
        {loadImages && (
          <div className="mx-auto flex justify-center py-20 px-10">
            <TEOLoader
              loaderSrc="/images/TEOloader.gif"
              className="w-[100px]"
            />
          </div>
        )}
        <PaginationPlusOne
          key={refresh}
          className={`mt-3 grid w-full grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 ${
            loadImages === true && ' hidden '
          }`}
          fetch={useGet.bind(null, `/imagelibraries`, {
            organisationId: `${authUser.activeOrganisationId}${
              useTeoLib ? ',OR,organisationId=null' : ''
            }`,
            onSuccess: (result) => {
              setLoadImages(false);
              const contentRange = result?.headers?.['content-range']
                ?.split(' ')?.[1]
                ?.split('/');
              const maxCount = contentRange ? Math.ceil(contentRange?.[1]) : 1;
              setNmOfImage(maxCount);
            },
          })}
          defaultSort={sort}
          empty={{
            template: 2,
            loadContent: loadImages,
            text: t('pages.create_library.library_empty'),
          }}
          page={0}
          pageSize={11}
          filter={filterJoined}
          render={(img) => {
            const arrTag = img?.tags?.split(';;');
            const imgTags = arrTag
              ?.filter((x) => x?.startsWith('tag:'))
              ?.map((el) => el?.split('tag:')[1]);
            return (
              <div key={img?.id} className="relative">
                <div
                  className="group max-w-[264px] cursor-pointer rounded-lg"
                  onClick={async () => {
                    setActiveImage(img);
                    setIsOpenAddLibraryModal(true);
                  }}
                >
                  <img
                    src={img?.imageUrl}
                    className="h-[200px] w-full rounded-lg object-cover object-center"
                  />
                  <div className="absolute right-2 top-2 hidden items-center justify-center gap-2 rounded-md bg-[#fff] px-3 py-1 group-hover:flex">
                    <i className="far fa-pencil text-xs"></i>
                    <p className="text-xs font-medium">{t('button.edit')}</p>
                  </div>
                  <div className="absolute left-2 bottom-2 hidden flex-wrap items-center justify-start gap-2 group-hover:flex">
                    {imgTags?.map((tag, i) => (
                      <div
                        key={i}
                        className="rounded-[4px] border border-white bg-[#777777a1]  px-2 py-[1px] text-xs text-white"
                      >
                        {tag}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          }}
          child={
            <div
              className="flex max-w-[264px] cursor-pointer rounded-lg border border-dashed bg-grey-01"
              onClick={() => setIsOpenNewLibraryModal(true)}
            >
              <div className="m-auto flex flex-col items-center justify-center gap-1">
                <div className=" rounded-full bg-white p-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-[#E4AD0F]"
                  >
                    <path d="M16 5h6"></path>
                    <path d="M19 2v6"></path>
                    <path d="M21 11.5V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7.5"></path>
                    <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"></path>
                    <circle cx="9" cy="9" r="2"></circle>
                  </svg>
                </div>
                <p>{t('pages.create_library.add_image')}</p>
              </div>
            </div>
          }
        />

        {isOpenAddLibraryModal && (
          <AddImageToLibrary
            imageUrl={activeImage?.imageUrl}
            imageName={activeImage?.imageName}
            isOpenAddLibraryModal={isOpenAddLibraryModal}
            setIsOpenAddLibraryModal={setIsOpenAddLibraryModal}
            editImage={activeImage}
            setEditImage={setActiveImage}
            edit={true}
            setRefresh={setRefresh}
          />
        )}

        {isOpenNewLibraryModal && (
          <NewImageToLibrary
            isOpenNewLibraryModal={isOpenNewLibraryModal}
            setIsOpenNewLibraryModal={setIsOpenNewLibraryModal}
            setRefresh={setRefresh}
          />
        )}
      </div>
    </div>
  );
};

export default ImageLibrary;
